import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AuthModal from "../components/auth/AuthModal";
import Logo from "../components/ui/Logo";
import useDarkMode from "../hooks/useDarkMode";
import "../styles/landing.css";

// ─────────────────────────────────────────────────────────────────────────────
// CONTENT
// Supported markup (kept intentionally simple):
//   ### Heading      → <h3>
//   #### Heading     → <h4>
//   - item           → <ul><li>
//   1. item          → <ol><li>
//   **bold**         → <strong>
//   TIP: text        → green tip callout
//   NOTE: text       → blue note callout
//   blank line       → paragraph spacing
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  // ── ACCOUNT ──────────────────────────────────────────────────────────────
  {
    id: "create-account",
    title: "Creating your account",
    icon: "👤",
    group: "Getting started",
    content: `
Baby Tracker is free to use. All you need is an email address and a password to get started — no credit card, no subscription.

### Step by step

1. Go to the Baby Tracker home page.
2. Click the **Get started free** button.
3. A sign-up box will appear. Enter your email address.
4. Choose a password — it must be at least 6 characters long.
5. Click **Create account**.
6. You will be taken straight into the dashboard. No email verification needed.

TIP: Use an email address you check regularly. If a partner or carer wants their own login, they will need to sign up with their own email address.
    `,
  },
  {
    id: "login-logout",
    title: "Logging in & out",
    icon: "🔐",
    group: "Getting started",
    content: `
### Logging in

1. Go to the Baby Tracker home page.
2. Click **Log in** in the top-right corner.
3. Enter the email address and password you used when you signed up.
4. Click **Log in**. You will be taken straight to your dashboard.

### Logging out

- **On desktop** — look for the **Log out** button at the very bottom of the left-hand sidebar.
- **On mobile** — tap the **More** button in the bottom tab bar, then scroll to the bottom of the drawer and tap **Log out**.

NOTE: Your data is saved automatically as you go. You do not need to do anything special before logging out.
    `,
  },
  {
    id: "add-child",
    title: "Adding your first child",
    icon: "👶",
    group: "Getting started",
    content: `
When you first log in, the dashboard will be empty. The first thing to do is add a child profile.

### Step by step

1. At the top of the screen you will see a pill-shaped button — it may say **No child selected** or show the name of an existing child.
2. Tap or click that pill to open the child switcher.
3. Click **Add Child**.
4. A form will appear asking for three things:
   - **Child's name** — for example, Emma or Oliver. This is required.
   - **Date of birth** — used to calculate your baby's age for sleep windows, growth charts, and milestone checklists. This is required.
   - **Gender** — optional. Choosing a gender changes the colour theme of the app (pink for girl, teal for boy, purple for other). This has no effect on any data or features.
5. Click **Add Child** to save.
6. The child profile will be selected automatically and the dashboard will load.

TIP: You can add more than one child — useful if you have twins or multiple children you are tracking. Each child has a completely separate set of data.
    `,
  },
  {
    id: "switching-children",
    title: "Switching between children",
    icon: "🔄",
    group: "Getting started",
    content: `
If you have more than one child profile, you can switch between them at any time.

### Step by step

1. Look for the child name pill near the top of the screen. It shows the currently selected child's name.
2. Tap or click it to open the child switcher panel.
3. Tap the name of the child you want to switch to.
4. The entire app — including today's summary, all tracking sections, charts, and insights — will update to show that child's data.

NOTE: The colour theme of the app will change when you switch to a different child, based on that child's gender setting.

### Deleting a child profile

1. Tap or click the child name pill to open the child switcher.
2. Find the child profile you want to delete.
3. Click the **delete (✕)** button next to their name and confirm when prompted.
4. This permanently deletes the child profile and all their tracking data. This cannot be undone.
    `,
  },
  {
    id: "dashboard",
    title: "Understanding the app layout",
    icon: "📊",
    group: "Getting started",
    content: `
### Navigation

- **Desktop** — a sidebar on the left lists all tracking sections. Click any item to navigate to that section's page. The sidebar also has **Edit profile**, **Restart tour**, and **Log out** at the bottom.
- **Mobile** — tap the **＋ Quick Log** floating button (bottom-right) to open the navigation sheet. It has two zones: **Quick Log** for one-tap logging, and **Navigate** for jumping between sections. The + button is accessible from every page.

### Top bar

The top bar is always visible and contains:
- **Child name pill** — tap it to switch child, add a child, or manage caregivers.
- **🔔 Bell** — open reminder settings.
- **🌙/☀️ Toggle** — switch between dark and light mode.

### Overview page

The **Overview** (home) page has several areas from top to bottom:

#### Today's Summary
A row of stat cards showing a live snapshot of today's activity — feeds, sleep, nappies, water, milestones, and more. The sleep card also shows a **sleep debt indicator** (how far below the age-appropriate daily target your baby currently is) when there is sleep data logged today.

#### Smart Insights
Automatically calculated observations including a **wake window indicator** (how long your baby has been awake vs. recommended), **sleep intelligence** (nap count, last nap time, bedtime consistency score), and **growth velocity** (recent weight and height changes).

#### Trends
Charts of daily totals — useful for spotting patterns in feeding volume, sleep duration, and more. Use the filter buttons above the charts to switch between time ranges.

#### Activity Feed
A real-time log of every entry made by all caregivers, showing who logged what and when. Use the filter pills above the feed to show only a specific type of entry (e.g. only nappy changes or only bottle feeds). Tap **Load more** to see older entries.

TIP: Each tracking section is its own page. Click a section in the sidebar or use the Quick Log FAB navigation sheet to open it.
    `,
  },

  // ── TRACKING ─────────────────────────────────────────────────────────────
  {
    id: "bottle-feeds",
    title: "Bottle feeds",
    icon: "🍼",
    group: "Tracking",
    content: `
The bottle feeds section lets you record every formula or expressed milk feed, including the amount and time.

### Adding a bottle feed entry

1. Navigate to **Bottle Feeds** in the sidebar or via the Quick Log FAB navigation sheet.
2. You will see a short form at the top of the page with the following fields:
   - **Amount** — how much your baby drank, in ounces or millilitres.
   - **Time** — what time the feed happened. This defaults to the current time, so if you are logging straight away you can leave it as is.
   - **Date** — defaults to today.
   - **Notes** — optional. Use this for anything worth remembering, such as which formula brand or how well the feed went.
3. Fill in the amount. Adjust the time and date if needed.
4. Click **Add Feed**. The entry will appear immediately in the table below the form.

### Editing an entry

1. Find the entry in the table.
2. Click the **edit (pencil) button** on the right side of that row.
3. A box will appear with the existing values pre-filled. Change whatever you need.
4. Click **Save** to confirm.

### Deleting an entry

1. Find the entry in the table.
2. Click the **delete (✕) button** on the right side of that row.
3. The entry is removed immediately.

### Exporting your bottle feed data

1. At the top of the Bottle Feeds section you will see two export buttons: **⬇ CSV** and **⬇ PDF**.
2. **CSV** downloads a spreadsheet file you can open in Excel or Google Sheets.
3. **PDF** downloads a formatted report with your baby's name and all entries.

### Today at a glance

At the top of the section you will see a compact summary bar showing four stats for the current day:

- **Feeds today** — how many feeds have been logged since midnight.
- **Total oz today** — the combined volume of all feeds logged today.
- **Last fed** — how long ago the most recent feed was (e.g. "2h ago").
- **Next feed** — a predicted time until the next feed is due, based on the average interval between your last several feeds. Shows "Due now" in red if the predicted window has already passed, or turns amber when fewer than 20 minutes remain.

TIP: Today's total feed count and volume is shown as a small badge next to the section title at the top of the page, so you can see it at a glance without scrolling.
    `,
  },
  {
    id: "breastfeeding",
    title: "Breastfeeding",
    icon: "🤱",
    group: "Tracking",
    content: `
The breastfeeding section lets you track nursing sessions including how long each session lasted and which side was used.

### Using the live timer (recommended)

1. Open the **Breast Feeding** section and tap **▶ Start Timer**.
2. A pink pill appears at the bottom of every screen showing the live elapsed time. You can navigate away — the timer keeps running.
3. When the session ends, tap **Stop & Log**.
4. The session is saved instantly with the duration rounded to the nearest minute. The entry appears in the table immediately.

### Adding a breastfeeding entry manually

1. Open the **Breast Feeding** section.
2. Fill in the form:
   - **Duration** — how many minutes the session lasted.
   - **Side** — left, right, or both.
   - **Time** — defaults to now.
   - **Date** — defaults to today.
   - **Notes** — optional. Useful for noting latch issues or discomfort.
3. Click **Add Session**.

### Editing and deleting

Use the edit and delete buttons on each row in the table.

### Today at a glance

At the top of the section you will see two indicators:

**Last side indicator** — a prominent bar showing which breast was used last and which to start on next. For example: "Last: LEFT → Start next on: RIGHT breast". This is the first thing nursing parents look for when a session is about to begin.

**Summary bar** — three stats for the current day: sessions today, total nursing time today, and how long ago the last session was.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons at the top of the section.
    `,
  },
  {
    id: "solid-foods",
    title: "Solid foods",
    icon: "🥘",
    group: "Tracking",
    content: `
Once your baby starts eating solids, use this section to log what they ate, how much, and how they reacted to new foods.

### Adding a solid food entry

1. Navigate to **Solid Foods** in the sidebar.
2. Fill in the form:
   - **Food name** — for example, "Sweet potato purée" or "Banana".
   - **Amount** — how much they ate (e.g. 2 tablespoons, half a pouch).
   - **Meal type** — breakfast, lunch, dinner, or snack.
   - **Time** — defaults to now.
   - **Date** — defaults to today.
   - **Reaction** — optional. Note any signs of reaction such as rash, fussiness, or refusal.
   - **Notes** — any other observations.
3. Click **Add Entry**.

### Editing and deleting

Use the edit and delete buttons on each row in the table, exactly as described in the Bottle Feeds section.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons at the top of the section.

TIP: This section is great for keeping a record when introducing new foods. Log each new food individually and note the date — then if a reaction appears you have a clear timeline to share with your doctor.
    `,
  },
  {
    id: "water-intake",
    title: "Water intake",
    icon: "💧",
    group: "Tracking",
    content: `
Track how much water your baby or toddler drinks throughout the day.

### Adding a water entry

1. Navigate to **Water Intake** in the sidebar.
2. Fill in the form:
   - **Amount** — in ounces or millilitres.
   - **Time** — defaults to now.
   - **Date** — defaults to today.
   - **Notes** — optional.
3. Click **Add Entry**.

### Editing and deleting

Use the edit and delete buttons in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

NOTE: Water intake tracking is typically used for babies over 6 months who have started solids. Babies under 6 months get all the hydration they need from milk.
    `,
  },
  {
    id: "sleep-tracking",
    title: "Sleep tracking",
    icon: "😴",
    group: "Tracking",
    content: `
Log every sleep session — night sleeps and daytime naps — to build a picture of your baby's sleep patterns.

### Using the live timer (recommended)

The easiest way to log sleep is with the built-in timer:

1. Open the **Sleep** section and tap **▶ Start Timer**.
2. A pink pill appears at the bottom of every screen showing the live elapsed time. You can navigate away and use other parts of the app — the timer keeps running.
3. When your baby wakes up, tap **Stop & Log** on the pill.
4. The sleep session is saved instantly — no form needed. The entry appears in the table immediately.

TIP: The type (night sleep or nap) is set automatically based on the time you started the timer — sessions started between 8 pm and 6 am are saved as night sleep; all others are saved as naps. You can edit the entry afterwards if needed.

### Adding a sleep entry manually

1. Open the **Sleep** section.
2. Fill in the form:
   - **Date** — the date the sleep started. Defaults to today.
   - **Start time** — what time your baby fell asleep.
   - **End time** — what time they woke up.
   - **Type** — Night sleep or Nap.
   - **Notes** — optional.
3. Click **Add Sleep**. The entry appears in the table below.

### Editing and deleting

Use the edit and delete buttons on each row in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

### Today at a glance

At the top of the section you will see a summary bar showing three stats:

- **Sleep today** — total sleep time logged since midnight (e.g. "4h 30m").
- **Naps today** — number of nap sessions logged today.
- **Last woke** — how long ago the most recent sleep session ended.

NOTE: The **Smart Insights** section on the Overview page uses your sleep data to show a wake window indicator — how long your baby has been awake and whether they are approaching nap time.
    `,
  },
  {
    id: "diapers",
    title: "Nappy changes",
    icon: "🧷",
    group: "Tracking",
    content: `
Keep a record of every nappy change, including the type, to monitor your baby's digestion and hydration.

### Adding a nappy entry

1. Navigate to **Nappies** in the sidebar.
2. Fill in the form:
   - **Type** — Wet, Dirty, or Both.
   - **Time** — defaults to now.
   - **Date** — defaults to today.
   - **Notes** — optional. Useful for noting unusual colour, consistency, or any concerns to mention at a doctor's appointment.
3. Click **Add Change**.

### Editing and deleting

Use the edit and delete buttons in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

### Today at a glance

At the top of the section you will see a summary bar with four stats: total changes today, wet count, dirty count, and how long ago the last change was logged.

**Stale nappy alert** — if no nappy change has been logged for 4 or more hours, a yellow warning banner appears at the top of the section asking whether a change is due. This is particularly useful overnight or during long gaps.

TIP: Today's nappy count is shown as a badge in the section header and in the Today's Summary cards at the top of the dashboard.
    `,
  },
  {
    id: "growth-health",
    title: "Growth & health",
    icon: "📏",
    group: "Tracking",
    content: `
Record your baby's weight and height at each measurement — whether at home or at a health visitor or doctor's appointment.

### Adding a growth entry

1. Navigate to **Growth & Health** in the sidebar.
2. Fill in the form:
   - **Date** — the date of the measurement.
   - **Weight** — in pounds/ounces or kilograms.
   - **Height / Length** — in inches or centimetres.
   - **Head circumference** — optional, in cm or inches.
   - **Notes** — optional. For example: "Measured at health visitor check-up".
3. Click **Add Entry**.

### Editing and deleting

Use the edit and delete buttons in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

### How measurements appear on the charts

Every measurement you log is plotted on the **Growth Percentiles** chart near the top of the dashboard. The chart shows where your baby sits relative to the World Health Organisation growth standards for their age and sex.

The **Growth Velocity** cards (just above the chart) show how much weight and height has changed in the last 7 days and last 30 days.

TIP: Even just one measurement per month gives the chart enough data to show a meaningful growth curve.
    `,
  },
  {
    id: "milestones",
    title: "Milestones",
    icon: "🎯",
    group: "Tracking",
    content: `
Record the developmental milestones your baby reaches — first smile, first word, first steps, and anything else you want to remember.

### Adding a milestone entry

1. Navigate to **Milestones** in the sidebar.
2. Make sure the **My Milestones** tab is selected.
3. Fill in the form:
   - **Milestone title** — a short description, for example: "First smile" or "Said 'mama' for the first time".
   - **Domain** — the area of development: Social & Emotional, Language & Communication, Cognitive, or Motor Skills. This is optional but helps organise entries.
   - **Date** — when it happened.
   - **Description** — optional. Add more detail or a memory you want to keep.
3. Click **Add Milestone**.

### Editing and deleting

Use the edit and delete buttons in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

TIP: You can also use the **Milestone Checklist** section (described next) to track milestones against an age-appropriate NHS development guide.
    `,
  },
  {
    id: "milestone-checklist",
    title: "Milestone checklist",
    icon: "✅",
    group: "Tracking",
    content: `
The milestone checklist gives you an age-appropriate list of developmental milestones to work through, based on your baby's current age. It is based on NHS developmental guidelines.

### How it works

1. Navigate to **Milestones** in the sidebar.
2. Tap the **NHS Checklist** tab near the top of the page.
3. The checklist automatically shows the milestones relevant to your baby's current age bracket — for example, 6 months, 9 months, 12 months, and so on.
4. Each milestone has a checkbox next to it. Tick it when your baby has achieved it.
5. Your progress is saved automatically and synced across all caregivers.
6. You can also browse other age brackets using the tabs above the checklist.

### Milestone domains

Milestones are grouped into four areas:
- **Social & Emotional** — bonding, expressing feelings, interacting with others.
- **Language & Communication** — sounds, words, understanding instructions.
- **Cognitive** — problem solving, memory, curiosity.
- **Motor Skills** — physical movement, coordination, and strength.

NOTE: This checklist is a helpful guide, not a diagnostic tool. Every baby develops at their own pace. If you have any concerns about your baby's development, speak to your health visitor or doctor.
    `,
  },
  {
    id: "doctor-visits",
    title: "Doctor visits",
    icon: "🏥",
    group: "Tracking",
    content: `
Keep a record of all your baby's health appointments in one place — GP visits, health visitor check-ups, hospital appointments, and specialist referrals.

### Adding a doctor visit entry

1. Navigate to **Doctor Visits** in the sidebar.
2. Fill in the form:
   - **Date** — the date of the appointment.
   - **Doctor / Clinic** — who you saw or where you went.
   - **Reason** — why you attended, for example: "12-week check-up" or "Ear infection".
   - **Notes** — what was discussed, any diagnoses, advice given, or follow-up required.
   - **Follow-up date** — optional. Set a reminder date for any next appointment.
3. Click **Add Visit**.

### Editing and deleting

Use the edit and delete buttons in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

TIP: The notes field is invaluable for keeping track of what was prescribed, what the doctor said to watch for, and any referrals made. You can refer back to these notes at future appointments.
    `,
  },
  {
    id: "vaccinations",
    title: "Vaccinations",
    icon: "💉",
    group: "Tracking",
    content: `
Keep an up-to-date record of all your baby's vaccinations, including what was given and when the next dose is due.

### Adding a vaccination entry

1. Navigate to **Vaccinations** in the sidebar.
2. Fill in the form:
   - **Vaccine name** — for example: "6-in-1", "MMR", or "Meningococcal B".
   - **Date given** — the date of the jab.
   - **Next due date** — optional. Enter the date for the next dose if one is required.
   - **Notes** — optional. For example: "Slight redness at injection site, settled after one day".
3. Click **Add Vaccination**.

### Editing and deleting

Use the edit and delete buttons in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

TIP: Keeping a complete vaccination record in the app means you always have it with you — useful when registering with a new GP, travelling, or if your paper red book is lost.
    `,
  },
  {
    id: "allergies",
    title: "Food allergies & reactions",
    icon: "⚠️",
    group: "Tracking",
    content: `
Record any known food allergies or adverse reactions your baby has had, including severity and symptoms.

### Adding an allergy entry

1. Navigate to **Allergies** in the sidebar.
2. Fill in the form:
   - **Allergen** — the food that caused the reaction, for example: "Cow's milk", "Peanut", or "Egg".
   - **Severity** — Mild, Moderate, or Severe.
   - **Reaction** — what symptoms appeared, for example: "Hives around mouth, settled within 30 minutes".
   - **Date discovered** — when the reaction first happened.
   - **Notes** — any additional detail, such as the specific product that triggered it or what treatment was given.
3. Click **Add Allergy**.

### Editing and deleting

Use the edit and delete buttons in the table.

### Exporting

Use the **⬇ CSV** or **⬇ PDF** buttons.

NOTE: The number of known allergens on file is shown in the Today's Summary cards as a reminder. This section is a personal record only — always follow the advice of your doctor or allergy specialist for diagnosis and management.
    `,
  },
  {
    id: "baby-journal",
    title: "Baby journal",
    icon: "📖",
    group: "Tracking",
    content: `
The baby journal is a free-form diary where you can write memories, observations, and stories about your baby's life. Unlike the other tracking sections, it is not about numbers — it is about capturing the moments you want to look back on.

### Adding a journal entry

1. Navigate to **Journal** in the sidebar.
2. Click **New Entry** (or the add button if shown).
3. A form will appear with the following fields:
   - **Title** — optional, a short heading for the entry.
   - **Entry** — the main text. Write as much or as little as you like.
   - **Date** — defaults to today.
   - **Photos** — optional. Tap the photo button to attach images from your device.
4. Click **Save Entry**.

### Editing and deleting

Existing entries appear as cards in a timeline below the form. Each card has edit and delete buttons.

### Sharing the journal

The journal can be shared with others in two ways — see the **Sharing the baby journal** section for full details.

### Exporting

Use the **⬇ PDF** button at the top of the Journal page to export your journal entries.

TIP: The journal is a lovely way to capture things that do not fit in the other tracking sections — a funny face they made, the first time they laughed, or a note about a difficult week. Future you will be very glad you wrote it down.
    `,
  },

  {
    id: "timeline",
    title: "Day timeline",
    icon: "📅",
    group: "Tracking",
    content: `
The Timeline page gives you a visual overview of a single day — everything that happened plotted on a 24-hour time axis. It is the fastest way to see patterns that lists and tables hide.

### Opening the timeline

Navigate to **Timeline** in the sidebar.

### What it shows

The timeline has two lanes:

- **Left lane (sleep bars)** — each sleep session appears as a coloured bar spanning the time it started and ended.
  - **Green bars** — naps.
  - **Blue/indigo bars** — night sleep.
- **Right lane (event dots)** — every other logged entry appears as a small dot with its time and a brief detail.
  - 🍼 Bottle feeds (with amount)
  - 🤱 Breastfeeding sessions (with duration)
  - 🧷 Nappy changes (with type)
  - 💧 Water intake (with amount)
  - 🥘 Solid food (with food name)

Hover over any bar or dot to see the full detail.

### Navigating between days

Use the **‹ Prev** and **Next ›** buttons at the top to move backward and forward one day at a time. The **Today** button jumps back to the current day. Future dates are disabled.

NOTE: The timeline shows data from six sections simultaneously. For entries to appear, they need to have been logged in their respective sections first.
    `,
  },

  // ── INSIGHTS ─────────────────────────────────────────────────────────────
  {
    id: "summary-insights",
    title: "Today's summary & smart insights",
    icon: "💡",
    group: "Insights & charts",
    content: `
### Today's Summary

The row of cards near the top of the dashboard shows a live snapshot of everything logged today:

- **Bottle feeds** — number of feeds and total volume.
- **Sleep** — total hours and minutes of sleep across all sessions.
- **Nappies** — total changes, split by wet and dirty.
- **Solid foods** — number of meals.
- **Water** — total volume.
- **Breastfeeding** — number of sessions and total minutes.
- **Milestones** — any milestones logged today.
- **Known allergens** — total on file (as a reminder, not a today-only count).

These update automatically as you add, edit, or delete entries throughout the day.

### Smart Insights

Below the summary cards you will find two insight panels:

#### Wake window indicator

This shows how long your baby has been awake since their last sleep ended, and compares it to the recommended wake window for their age.

- **Green** — within the normal range, no nap needed yet.
- **Amber** — approaching the upper limit, a nap will be needed soon.
- **Red** — past the recommended wake time, your baby is likely overtired.

The recommended windows are based on typical ranges for each age:
- Newborn to 6 weeks: 45 minutes to 1 hour
- 6 weeks to 3 months: 1 to 1.5 hours
- 3 to 6 months: 1.5 to 2 hours
- 6 to 9 months: 2 to 3 hours
- And so on up to toddler age.

NOTE: These are general guidelines. All babies are different. Use it as a prompt, not a rule.

#### Growth velocity

Shows rate-of-change cards next to the percentile chart:
- **7-day weight change** — how many grams or ounces gained or lost in the last week.
- **30-day weight change** — the same over the last month.
- **7-day height change** and **30-day height change**.

A warning appears if your last measurement is more than two weeks old.
    `,
  },
  {
    id: "charts",
    title: "Trend charts & growth percentiles",
    icon: "📈",
    group: "Insights & charts",
    content: `
### Trend charts

The Trends & Insights section shows line and bar charts of your baby's daily totals over the last 1–2 weeks. Charts include:

- **Daily bottle volume** — total oz or ml per day.
- **Daily sleep duration** — total hours of sleep per day.
- **Daily nappy count** — total changes per day.
- **Solid food meals** — number of meals per day.

These charts update automatically as you log new entries. They are most useful after you have a week or more of data.

### Growth percentile chart

The Growth Percentiles section shows a chart with:
- Your baby's actual weight and height measurements plotted as dots over time.
- Five reference curves from the World Health Organisation: 3rd, 15th, 50th, 85th, and 97th percentile.

**What the percentiles mean:** If your baby is on the 50th percentile for weight, it means half of babies their age weigh less and half weigh more. Being on the 10th or 90th percentile is completely normal — what matters is that the curve is consistent over time.

To get data on the chart, add measurements in the **Growth & Health** section. Even a single measurement will appear as a point.

NOTE: The chart uses WHO standards, which are widely used by health visitors and GPs in the UK and internationally. The chart is a guide — for any concerns about growth, speak to your health visitor or doctor.
    `,
  },

  {
    id: "activity-feed",
    title: "Activity feed",
    icon: "📋",
    group: "Insights & charts",
    content: `
The activity feed appears on the Overview page and shows a real-time log of every entry made across all caregivers — who logged what, and when.

### What it shows

Each entry in the feed displays:
- The section icon and name (e.g. 🍼 Bottle Feeds, 😴 Sleep).
- A summary of the entry (e.g. "180 ml · formula").
- The caregiver's name and role (e.g. "Sarah · Mum") — or "You" if it was you.
- How long ago it was logged.

### Why it is useful

If you share access with a partner, nanny, or grandparent, the activity feed lets you see at a glance what has been logged without needing to message each other. You can see "Dad logged a 180 ml feed 20 minutes ago" before you offer another feed.

### Setting your name and role

For your name and role to appear in the feed, set up your profile — see the **Your profile** section for details. Without a profile, your entries will show your email address instead.

NOTE: The activity feed shows entries from all caregivers who have access to the selected child. It is not limited to your own entries.
    `,
  },

  // ── SHARING ──────────────────────────────────────────────────────────────
  {
    id: "caregivers",
    title: "Adding caregivers",
    icon: "👥",
    group: "Sharing & access",
    content: `
A caregiver is anyone you want to give full access to your baby's dashboard — a partner, grandparent, nanny, or childminder. They can view all data and add new entries, exactly the same as you.

### Adding a caregiver

1. Tap the **child name pill** near the top of the screen to open the child switcher.
2. Find the **Manage caregivers** or **👥** option next to the child's name.
3. The Caregivers panel will open.
4. In the **Add a caregiver** field at the bottom, type their email address.
5. Click **Add**.
6. An invite link will be generated automatically and appear next to their email.
7. Click **Copy** to copy the link to your clipboard.
8. Share it with the caregiver however you like — a text message, WhatsApp, or email.

### What happens when they follow the link

When the caregiver opens the invite link:
1. They will see a page saying who invited them and which child they have been invited to help track.
2. Their email address will already be filled in.
3. If they are new, they click **Create account & accept** and choose a password.
4. If they already have a Baby Tracker account, they click **I already have an account — log in**.
5. After signing in, they land on the full dashboard for that child immediately. No further steps needed.

### Removing a caregiver

1. Tap the **child name pill** to open the child switcher, then open the caregivers panel.
2. Find the caregiver in the list.
3. Click **Remove** next to their name.
4. Their access is revoked immediately. The next time they open the app, they will not see that child's data.

TIP: You can have as many caregivers as you need. Each one has their own account and their own login — they do not share a password with you.
    `,
  },
  {
    id: "journal-sharing",
    title: "Sharing the baby journal",
    icon: "🔗",
    group: "Sharing & access",
    content: `
The baby journal can be shared in two ways that are separate from the full caregiver access system. Both options give read-only access — the recipient can read entries and see photos but cannot add or edit anything.

### Option 1 — Share by email (viewer access)

This is for people you want to give ongoing read access to the journal — for example, grandparents who live far away.

1. Inside the **Baby Journal** section, click the **Share Journal** button.
2. Select the **Email Access** tab.
3. Enter the person's email address and click **Add**.
4. They will need to create a Baby Tracker account using that email address (or log in if they already have one).
5. Once logged in, they will see the journal for that child listed in their dashboard. The dashboard will show a notice that they have read-only access.

To remove someone's viewer access, open the Share Journal panel, find their email, and click **Remove**.

### Option 2 — Public link

A public link lets anyone with the link view the journal — no account required. This is ideal for sharing with extended family or friends.

1. Inside the **Baby Journal** section, click the **Share Journal** button.
2. Select the **Public Link** tab.
3. Toggle the switch to **Enabled**.
4. A link will be generated. Click **Copy** to copy it.
5. Share the link with whoever you like.
6. Anyone with the link can view the journal in a read-only format.

To disable the link, toggle the switch off. The link will stop working immediately. You can also click **Regenerate** to create a new link — the old link will stop working and anyone you want to continue sharing with will need the new one.

NOTE: Anyone with the public link can view the journal without logging in. Do not share it publicly (e.g. on social media) if you have any privacy concerns.
    `,
  },

  // ── EXPORTING ─────────────────────────────────────────────────────────────
  {
    id: "exporting",
    title: "Exporting your data",
    icon: "⬇️",
    group: "Settings & tools",
    content: `
All your tracking data can be exported at any time, either as a spreadsheet or a formatted PDF report.

### Export a single section

Every tracking section has two export buttons in its header:

- **⬇ CSV** — downloads a comma-separated file you can open in Microsoft Excel, Google Sheets, or Apple Numbers.
- **⬇ PDF** — downloads a formatted PDF report showing all entries for that section, including your baby's name.

### Export everything at once

Each section has its own CSV and PDF export buttons. To get a single report covering all sections, use the PDF export button available in individual section headers — or export each section separately and combine them.

TIP: There is no limit on how often you can export. Your data always stays in the app — exporting creates a copy, it does not remove anything.
    `,
  },
  {
    id: "customising",
    title: "Customising your dashboard",
    icon: "⚙️",
    group: "Settings & tools",
    content: `
You can choose which tracking sections appear on your dashboard and arrange them in the order that suits you best.

### Opening the customiser

1. Go to the **Overview** page.
2. Click the **⚙ Customise** button near the top of the page.
3. The dashboard customiser panel will open.

### Showing and hiding sections

In the customiser, you will see a list of all available tracking sections. Each one has a toggle switch.

- Toggle a section **on** to show it on the dashboard.
- Toggle it **off** to hide it.

Hidden sections do not lose any data — they are just not shown on the dashboard. You can turn them back on at any time.

### Changing the order

In the customiser, drag and drop sections up or down to change the order in which they appear on the dashboard.

### Saving

Click **Save** to apply your changes. The dashboard will update immediately.

TIP: If you are only using certain sections (for example, you are exclusively breastfeeding and do not need the bottle feeds section), hiding the sections you do not use makes the dashboard cleaner and faster to navigate.
    `,
  },
  {
    id: "live-timer",
    title: "Live timer",
    icon: "⏱️",
    group: "Settings & tools",
    content: `
The live timer lets you time sleep sessions and breastfeeding sessions in real time, then log them with a single tap — no form filling required.

### Starting the timer

1. Navigate to the **Sleep** or **Breast Feeding** section.
2. Tap the **▶ Start Timer** button near the top of the section.
3. A pink floating pill appears at the bottom of every screen showing the session type, a pulsing dot, and the live elapsed time.

### The timer pill

Once running, the pill is visible from every page in the app — you do not need to stay on the Sleep or Breast Feeding page. You can log a nappy change, check the feed history, or navigate anywhere while the timer continues in the background.

The pill has two buttons:
- **✕** — cancel the timer without saving anything.
- **Stop & Log** — stop the timer and save the entry immediately.

### Stopping and logging

Tap **Stop & Log**. The entry is written directly to Firestore — no form appears, no submit button to press. The pill briefly shows a green confirmation, then disappears. The new entry appears instantly in the relevant section's table.

### What gets saved

- **Sleep** — start time, end time, and duration. The type (Night sleep or Nap) is set automatically: sessions started between 8 pm and 6 am are saved as night sleep, all others as naps.
- **Breastfeeding** — start time and duration (rounded to the nearest minute). Side defaults to "left" — edit the entry afterwards if needed.

### Only one timer at a time

You can only run one timer at a time. If you try to start a second one, the first will still be running — cancel or stop it first.

TIP: The timer state is saved to the cloud in real time, not just your device. If you close the browser, switch to another caregiver's phone, or refresh the page, the timer will still be running. All caregivers see the same live timer simultaneously.
    `,
  },
  {
    id: "quick-log-fab",
    title: "Quick Log button",
    icon: "⚡",
    group: "Settings & tools",
    content: `
The Quick Log button (the **＋** floating button at the bottom-right of every page on mobile) is the fastest way to log common events and navigate the app without using the sidebar.

### Opening the Quick Log sheet

Tap the **＋** button. A bottom sheet slides up with two zones.

### Quick Log zone

Log the most common events in one or two taps:

- **Wet nappy** — logs a wet nappy immediately with no form.
- **Dirty nappy** — logs a dirty nappy immediately.
- **Bottle** — shows a small amount adjuster (±0.5 oz). Tap + or − to set the amount, then tap **Log**. The form pre-fills with the previous feed's amount and type.
- **Start nap timer** — starts a sleep timer immediately.
- **Start breastfeed timer** — starts a breastfeeding timer immediately.

### Navigate zone

A full list of all sections. Tap any section name to go straight to that page. This replaces the bottom tab bar on mobile.

### Closing the sheet

Tap anywhere outside the sheet, or tap the **＋** button again (it becomes a **✕** while open).

TIP: The Quick Log sheet is only shown on mobile screens. On desktop, use the sidebar for navigation and the section forms for logging.
    `,
  },
  {
    id: "your-profile",
    title: "Your profile",
    icon: "👤",
    group: "Settings & tools",
    content: `
Your profile lets you set a display name and caregiver role so other caregivers can see who logged each entry in the activity feed.

### Setting up your profile

When you first log in, a profile setup prompt will appear automatically. You can:
- Enter your **name** (e.g. "Sarah" or "Grandma Jean").
- Select your **role** from a grid: Mum, Dad, Nan, Grandad, Nanny, Partner, Carer, or Other.
- Click **Save profile**.

Or click **Skip for now** — the prompt will not appear again, but you can always set it up later.

### Editing your profile

1. On **desktop** — look for the **👤 Edit profile** button at the bottom of the left-hand sidebar.
2. On **mobile** — tap **More** in the bottom tab bar, then look for the Edit profile option.
3. The profile setup modal will open with your current details.
4. Update your name or role and click **Save profile**.

### How it appears to others

Once your profile is set, every entry you log will show your name and role in the **Activity Feed** — for example, "Kieron · Dad". Without a profile set, your email address prefix is shown instead.

NOTE: Your profile is personal to your account. It is not visible to anyone outside of the caregiver group for your child.
    `,
  },
  {
    id: "dark-mode",
    title: "Dark mode & colour themes",
    icon: "🌙",
    group: "Settings & tools",
    content: `
### Switching dark mode on or off

1. Look at the header bar at the very top of the app.
2. Click the **🌙 moon button** to switch to dark mode.
3. Click the **☀️ sun button** to switch back to light mode.
4. Your preference is remembered — the app will open in the same mode next time.

Dark mode uses deep navy backgrounds with lighter text, reducing eye strain in low-light conditions. It is especially useful for night feeds.

### Colour themes

The dashboard colour theme changes automatically based on the gender you set for each child:

- **Girl** — soft pink and rose tones.
- **Boy** — steel blue and teal tones.
- **Other** — muted purple tones.
- **Not set** — default pink.

The theme changes when you switch between children. All colours are designed to be clearly readable in both light and dark mode.

NOTE: The colour theme is visual only. It has no effect on any features, data, or functionality.
    `,
  },
  {
    id: "offline",
    title: "Using the app offline",
    icon: "✈️",
    group: "Settings & tools",
    content: `
Baby Tracker works without an internet connection. You can log entries on a plane, in a hospital with no signal, or in a rural area — and everything will sync when you are back online.

### How it works

- When you open the app with a connection, it saves a local copy of your data on your device.
- If you then lose signal, the app continues to work normally from that local copy.
- Any new entries you add while offline are saved on your device.
- As soon as you are back online, all queued entries sync automatically. Nothing is lost.

### The offline banner

A dark bar appears at the bottom of the screen when you are offline. It says:

> "📡 You're offline. New entries will be saved and synced when you reconnect."

When your connection returns, the bar briefly turns green and shows:

> "✓ Back online — all changes synced"

Then it disappears.

### Things that need a connection

- **Photo uploads** in the baby journal require a connection. The text entry will save offline, but photos will only upload when you are back online.
- **Sharing features** (generating invite links, generating public journal links) require a connection.

### Best practice for planned offline periods

If you know you will be without signal — for example, a long-haul flight or a trip to a remote area — open the app once while you still have signal. This ensures the latest data is saved locally and ready to use.

TIP: The app works best if you keep it open in your browser rather than closing it between uses. On a phone, you can add it to your home screen as a shortcut so it always opens quickly.
    `,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// RENDERER
// Parses the plain-text content strings into React elements.
// Groups consecutive list items into <ul> or <ol> blocks.
// ─────────────────────────────────────────────────────────────────────────────

function inlineBold(text, key) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return text;
  return (
    <span key={key}>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**")
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : part
      )}
    </span>
  );
}

function renderContent(text) {
  const lines = text.trim().split("\n");
  const output = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { i++; continue; }

    // h3
    if (trimmed.startsWith("### ")) {
      output.push(<h3 key={i} className="docs-h3">{trimmed.slice(4)}</h3>);
      i++; continue;
    }
    // h4
    if (trimmed.startsWith("#### ")) {
      output.push(<h4 key={i} className="docs-h4">{trimmed.slice(5)}</h4>);
      i++; continue;
    }
    // TIP
    if (trimmed.startsWith("TIP:")) {
      output.push(
        <div key={i} className="docs-tip">
          <span className="docs-tip-label">Tip</span>
          {inlineBold(trimmed.slice(4).trim(), `tip-${i}`)}
        </div>
      );
      i++; continue;
    }
    // NOTE
    if (trimmed.startsWith("NOTE:")) {
      output.push(
        <div key={i} className="docs-note">
          <span className="docs-note-label">Note</span>
          {inlineBold(trimmed.slice(5).trim(), `note-${i}`)}
        </div>
      );
      i++; continue;
    }
    // unordered list — collect consecutive
    if (trimmed.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(<li key={i}>{inlineBold(lines[i].trim().slice(2), i)}</li>);
        i++;
      }
      output.push(<ul key={`ul-${i}`} className="docs-list">{items}</ul>);
      continue;
    }
    // ordered list — collect consecutive
    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        const text2 = lines[i].trim().replace(/^\d+\.\s/, "");
        items.push(<li key={i}>{inlineBold(text2, i)}</li>);
        i++;
      }
      output.push(<ol key={`ol-${i}`} className="docs-list">{items}</ol>);
      continue;
    }
    // default paragraph
    output.push(<p key={i} className="docs-para">{inlineBold(trimmed, i)}</p>);
    i++;
  }
  return output;
}

// ─────────────────────────────────────────────────────────────────────────────
// GROUP sidebar sections by their group label
// ─────────────────────────────────────────────────────────────────────────────
const GROUPS = [...new Set(SECTIONS.map((s) => s.group))];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function DocsPage() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [authOpen, setAuthOpen]           = useState(false);
  const [authMode, setAuthMode]           = useState("login");
  const [isDark, toggleDark]              = useDarkMode();
  const navigate                          = useNavigate();
  const contentRef                        = useRef(null);

  const openLogin  = () => { setAuthMode("login");  setAuthOpen(true); };
  const openSignup = () => { setAuthMode("signup"); setAuthOpen(true); };

  // Update active section based on scroll position
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="landing-root">

      {/* ── NAV ─────────────────────────────────────────────────────── */}
      <nav className="landing-nav">
        <a href="/"><Logo variant="full" size={32} /></a>
        <div className="landing-nav-actions">
          <button className="btn btn-sm landing-nav-theme" onClick={toggleDark} aria-label="Toggle dark mode">
            {isDark ? "☀️" : "🌙"}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={openLogin}>Log in</button>
          <button className="btn btn-sm btn-primary"            onClick={openSignup}>Sign up free</button>
        </div>
      </nav>

      {/* ── PAGE HEADER ─────────────────────────────────────────────── */}
      <div className="docs-page-header">
        <div className="landing-container">
          <h1 className="docs-page-title">Help & Documentation</h1>
          <p className="docs-page-sub">Step-by-step guides for every feature in Baby Tracker.</p>
        </div>
      </div>

      {/* ── DOCS LAYOUT ─────────────────────────────────────────────── */}
      <div className="docs-layout">

        {/* Sidebar */}
        <aside className="docs-sidebar">
          {GROUPS.map((group) => (
            <div key={group} className="docs-sidebar-group">
              <p className="docs-sidebar-label">{group}</p>
              {SECTIONS.filter((s) => s.group === group).map((s) => (
                <button
                  key={s.id}
                  className={`docs-sidebar-link${activeSection === s.id ? " active" : ""}`}
                  onClick={() => handleNavClick(s.id)}
                >
                  <span className="docs-sidebar-icon">{s.icon}</span>
                  {s.title}
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Content */}
        <main className="docs-content" ref={contentRef}>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="docs-section">
              <div className="docs-section-title-row">
                <span className="docs-section-icon">{s.icon}</span>
                <h2 className="docs-section-heading">{s.title}</h2>
              </div>
              <div className="docs-body">{renderContent(s.content)}</div>
            </section>
          ))}

          <div className="docs-cta-box">
            <p>Ready to start tracking?</p>
            <button className="btn btn-primary" onClick={openSignup}>Create a free account</button>
          </div>
        </main>
      </div>

      {/* ── FOOTER ──────────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <Logo variant="full" size={28} />
          <nav className="landing-footer-links">
            <a href="/">Home</a>
            <a href="/roadmap">Roadmap</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <button className="landing-footer-btn-link" onClick={openLogin}>Log in</button>
          </nav>
        </div>
      </footer>

      {authOpen && (
        <AuthModal
          initialMode={authMode}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { setAuthOpen(false); navigate("/app"); }}
        />
      )}
    </div>
  );
}
